import { HTMLAttributes, useEffect, useMemo, useRef } from 'react'
import { Transition } from 'react-transition-group'
import { useKey } from 'react-use'
import Link from 'next/link'

import Button from '@/components/base/button'
import LangSelector from '@/components/base/lang-selector'
import Logo from '@/components/base/logo'
import SVGIcon from '@/components/base/svg-icon'
import Typography from '@/components/base/typography'
import TypographyMask from '@/components/base/typography-mask'
import Menu from '@/components/layout/menu'
import { useStore } from '@/contexts/store'
import { useTheme } from '@/contexts/theme'
import { useLayoutStore } from '@/store/layout'
import { useRouterStore } from '@/store/router'

import { cn } from '@/lib/utils'

export type HeaderProps = HTMLAttributes<HTMLElement>

const Header = ({ ...props }: HeaderProps) => {
  const { strings } = useStore()
  const { links, labels } = strings || {}
  const { theme: context } = useTheme()
  const menu = useRef<HTMLDivElement>(null)
  const theme = useMemo(() => context?.header.variant || 'dark', [context])
  const menuOpen = useLayoutStore((state) => state.menuOpen)
  const setLayoutState = useLayoutStore((state) => state.setLayoutState)

  useKey('Escape', () => setLayoutState({ menuOpen: false }))

  useEffect(
    () => setLayoutState({ lockScroll: menuOpen }),
    [menuOpen, setLayoutState]
  )

  useEffect(() => {
    const unsubscribe = useRouterStore.subscribe(
      (state) => state.currentRoute,
      () => setLayoutState({ menuOpen: false })
    )

    return () => {
      unsubscribe()
    }
  }, [setLayoutState])

  return (
    <>
      <header
        className={cn(
          'fixed left-0 top-0 z-header flex h-[var(--header-height)] w-full border-b py-[2rem] md:py-[3rem]',
          'border-transparent bg-transparent',
          'transition-all duration-500 ease-in-out-cubic',
          {
            'border-transparent bg-transparent': theme === 'light',
            'border-havana bg-light': theme === 'dark'
          },
          '[.header-contained_&]:border-havana [.header-contained_&]:bg-light',
          '[.header-collapsed_&]:h-[var(--header-height-collapsed)]',
          '[.page-transition_&]:border-transparent [.page-transition_&]:bg-transparent'
        )}
        {...props}
      >
        <button
          className={cn(
            'absolute left-margin top-1/2 -translate-y-1/2',
            'group -mx-[1rem] flex items-center p-[1rem] font-gillsans uppercase md:space-x-[1.5rem]',
            'transition-colors duration-500 ease-in-out-cubic',
            {
              'text-light': theme === 'light',
              'text-dark': theme === 'dark'
            },
            '[.header-contained_&]:text-dark',
            '[.page-transition_&]:text-dark'
          )}
          onClick={() => setLayoutState({ menuOpen: !menuOpen })}
        >
          <span
            className={cn(
              'relative hidden flex-col md:flex',
              'before:block before:h-[1px] before:w-[6rem] before:-translate-y-[.3rem] before:bg-current',
              'after:block after:h-[1px] after:w-[6rem] after:translate-y-[.3rem] after:bg-current',
              'before:transition-transform before:delay-150 before:duration-500 before:ease-out-cubic',
              'after:transition-transform after:delay-75 after:duration-500 after:ease-out-cubic',
              'group-hover:before:-translate-x-1/2 group-hover:before:scale-x-0 group-hover:before:delay-75',
              'group-hover:after:-translate-x-1/2 group-hover:after:scale-x-0 group-hover:after:delay-0'
            )}
          >
            <span
              className={cn(
                'absolute left-0 top-0 flex flex-col',
                'before:block before:h-[1px] before:w-[6rem] before:-translate-y-[.3rem] before:translate-x-1/2 before:scale-x-0 before:bg-current',
                'after:block after:h-[1px] after:w-[6rem] after:translate-x-1/2 after:translate-y-[.3rem] after:scale-x-0 after:bg-current',
                'before:transition-transform before:delay-75 before:duration-500 before:ease-out-cubic',
                'after:transition-transform after:delay-0 after:duration-500 after:ease-out-cubic',
                'group-hover:before:translate-x-0 group-hover:before:scale-x-100 group-hover:before:delay-150',
                'group-hover:after:translate-x-0 group-hover:after:scale-x-100 group-hover:after:delay-75'
              )}
            />
          </span>
          <TypographyMask>
            <Typography variant="label">{labels?.menu}</Typography>
          </TypographyMask>
        </button>

        <Link
          href="/"
          scroll={false}
          className="absolute left-1/2 top-1/2 z-10"
        >
          <span className="sr-only">{strings?.brand}</span>
          <Logo
            theme={theme}
            symbol="logo-icon"
            className={cn(
              'absolute left-0 top-0 -translate-x-1/2 -translate-y-1/4',
              'lg:transition-all lg:duration-500 lg:ease-in-out-cubic',
              'lg:-translate-y-1/2 lg:[.header-collapsed_&]:-translate-y-1/4',
              '[.header-contained_&]:fill-dark',
              '[.page-transition_&]:fill-dark'
            )}
          />
          <Logo
            theme={theme}
            symbol="logo-text"
            className={cn(
              'absolute left-0 top-0 hidden -translate-x-1/2 -translate-y-1/2',
              'lg:flex lg:transition-all lg:duration-500 lg:ease-in-out-cubic',
              'lg:[.header-collapsed_&]:invisible lg:[.header-collapsed_&]:-translate-y-1/4 lg:[.header-collapsed_&]:opacity-0',
              '[.header-contained_&]:fill-dark',
              '[.page-transition_&]:fill-dark'
            )}
          />
        </Link>

        <div
          className={cn(
            'absolute right-margin top-1/2 flex -translate-y-1/2 items-center justify-end space-x-[1rem]',
            'w-[calc(50%_-_var(--margin))]'
          )}
        >
          <div
            className={cn(
              'mr-[1rem] flex -translate-y-[0.1rem] xl:mx-auto xl:translate-x-[5rem]',
              {
                'text-light': theme === 'light',
                'text-dark': theme === 'dark'
              },
              '[.header-contained_&]:text-dark',
              '[.page-transition_&]:text-dark'
            )}
          >
            <LangSelector />
          </div>

          <Button
            variant="icon"
            theme={theme}
            asChild
            className={cn(
              '[.header-contained_&]:text-light [.header-contained_&]:before:bg-dark',
              '[.page-transition_&]:text-light [.page-transition_&]:before:bg-dark'
            )}
          >
            <Link href={links?.phone.url} target={links?.phone.target}>
              <span className="sr-only">{links?.book.title}</span>
              <SVGIcon
                symbol="phone-icon"
                className="relative h-[1.6rem] w-[1.6rem]"
              />
            </Link>
          </Button>

          <Button
            theme={theme}
            asChild
            className={cn(
              'hidden md:flex',
              '[.header-contained_&]:text-light [.header-contained_&]:before:bg-dark',
              '[.page-transition_&]:text-light [.page-transition_&]:before:bg-dark'
            )}
          >
            <Link
              href={links?.book.url}
              target={links?.book.target}
              scroll={false}
            >
              <TypographyMask>
                <Typography variant="label">{links?.book.title}</Typography>
              </TypographyMask>
            </Link>
          </Button>
        </div>
      </header>

      <Transition
        nodeRef={menu}
        in={menuOpen}
        timeout={{
          enter: 0,
          exit: 500
        }}
        unmountOnExit
      >
        {(state) => <Menu ref={menu} state={state} />}
      </Transition>
    </>
  )
}

export default Header
